// FILE: apps/admin/src/pages/Dashboard.tsx
// VERSION: 2.2.0
// START_MODULE_CONTRACT
//   PURPOSE: Admin Dashboard page with 100% real DB/OS metrics, 4 KPI cards, SVG area/line chart, enlarged 224x224 category donut chart, official Stars/GRAM SVG badges, recent orders table, quick actions rail, and live system telemetry.
//   SCOPE: Dashboard component and normalizeDashboardOrder helper.
//   DEPENDS: ../api.js, ../components/DailyStatsChart.js, ../components/Icons.js, ../display-currency.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   Dashboard - Main operator dashboard screen backed by live DB and OS metrics
//   NormalizedDashboardOrder - Display-ready normalized order row for Dashboard tables
//   RawDashboardOrderRow - Raw order summary payload shape from /admin/orders
//   normalizeDashboardOrder - Safely normalize /admin/orders summary rows for Dashboard rendering
// END_MODULE_MAP

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api, num } from '../api.js';
import { type DailyStatPoint, DailyStatsChart } from '../components/DailyStatsChart.js';
import { AdminIcon } from '../components/Icons.js';
import { useDisplayCurrency } from '../display-currency.js';

interface KpiMetric {
  key: string;
  label: string;
  value: number;
  previousValue: number;
  deltaPercent: number;
  unit: string;
}

interface ReconcileSnapshot {
  generatedAt: string;
  healthy: boolean;
  balanceDrifts: Array<Record<string, unknown>>;
  gramDrifts: Array<Record<string, unknown>>;
  stuckOrders: Array<Record<string, unknown>>;
  unmatchedPaymentsCount24h: number;
  unmatchedPaymentsTotalKopecks24h: number;
  openSupportTicketsCount: number;
  pendingBanAppealsCount: number;
}

interface CategoryBreakdownRow {
  key: string;
  label: string;
  ordersCount: number;
  revenueKopecks: number;
}

interface DashboardResponse {
  byStatus: Record<string, number>;
  revenueKopecks: number;
  revenue7dKopecks: number;
  starsDelivered: number;
  premiumDelivered: number;
  ordersToday: number;
  orders7d: number;
  users: number;
  outstandingBalanceKopecks: number;
  sessions: { tbank: string; tbankUrl: string; fragment: string };
  kpis?: KpiMetric[];
  dailySeries?: DailyStatPoint[];
  categoryBreakdown?: CategoryBreakdownRow[];
  systemMetrics?: {
    cpuLoadPercent: number;
    memoryUsedPercent: number;
    uptimeSeconds: number;
  };
  queues?: {
    awaitingOrdersCount: number;
    stuckOrdersCount: number;
    unmatchedPaymentsCount: number;
    openSupportTicketsCount: number;
    pendingBanAppealsCount: number;
  };
  reconcile?: ReconcileSnapshot;
}

export interface RawDashboardOrderRow {
  id?: string;
  shortId?: string;
  userId?: string | null;
  telegramId?: number | null;
  username?: string | null;
  product?: string;
  starsQty?: number | null;
  premiumMonths?: number | null;
  qty?: number | null;
  recipientUsername?: string | null;
  amountKopecks?: number | null;
  balanceAppliedKopecks?: number | null;
  expectedAmountKopecks?: number | null;
  status?: string;
  createdAt?: string;
}

export interface NormalizedDashboardOrder {
  id: string;
  shortCode: string;
  userDisplay: string;
  userSubId: string;
  badgeKind: 'stars' | 'premium' | 'gram';
  badgeIcon: string;
  serviceTitle: string;
  totalKopecks: number;
  statusLabel: string;
  statusClass: string;
  relativeTime: string;
}

function DeltaPill({ delta }: { delta?: number }) {
  // START_BLOCK_DASHBOARD_DELTA_PILL
  const val = typeof delta === 'number' && Number.isFinite(delta) ? delta : 0;
  if (val === 0) {
    return (
      <span className="mp-kpi-delta" style={{ color: 'var(--ink-faint)' }}>
        0.0%
      </span>
    );
  }
  const up = val > 0;
  return (
    <span className={`mp-kpi-delta ${up ? 'up' : 'down'}`}>
      {up ? '+' : ''}
      {val.toFixed(1)}%
    </span>
  );
  // END_BLOCK_DASHBOARD_DELTA_PILL
}

function formatRelativeTime(iso?: string): string {
  // START_BLOCK_DASHBOARD_RELATIVE_TIME
  if (!iso) return 'только что';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return 'недавно';
  const diffSec = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (diffSec < 60) return `${diffSec} сек назад`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} мин назад`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} ч назад`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} дн назад`;
  // END_BLOCK_DASHBOARD_RELATIVE_TIME
}

function mapOrderStatusRu(status?: string): { label: string; cls: string } {
  // START_BLOCK_DASHBOARD_STATUS_MAP
  switch (status) {
    case 'DELIVERED':
      return { label: 'Выполнен', cls: 'DELIVERED' };
    case 'AWAITING_PAYMENT':
    case 'MATCHING':
    case 'MATCHED':
    case 'DELIVERING':
      return { label: 'В обработке', cls: 'AWAITING_PAYMENT' };
    case 'DELIVERY_FAILED':
      return { label: 'Ошибка', cls: 'DELIVERY_FAILED' };
    case 'EXPIRED':
    case 'CANCELLED':
    default:
      return { label: 'Отменён', cls: 'EXPIRED' };
  }
  // END_BLOCK_DASHBOARD_STATUS_MAP
}

// START_CONTRACT: normalizeDashboardOrder
//   PURPOSE: Safely normalize an order summary object from /admin/orders into display-ready fields without assuming optional fields exist.
//   INPUTS: RawDashboardOrderRow
//   OUTPUTS: NormalizedDashboardOrder
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: normalizeDashboardOrder
export function normalizeDashboardOrder(o: RawDashboardOrderRow): NormalizedDashboardOrder {
  // START_BLOCK_NORMALIZE_DASHBOARD_ORDER
  const id = typeof o.id === 'string' && o.id ? o.id : (o.shortId ?? '000000');
  const shortCode = (o.shortId || id.slice(0, 6)).toUpperCase();
  const userDisplay = o.username || o.recipientUsername || 'Пользователь';
  const userSubId =
    o.telegramId !== undefined && o.telegramId !== null
      ? String(o.telegramId)
      : typeof o.userId === 'string' && o.userId.length > 0
        ? o.userId.slice(0, 6)
        : shortCode;

  const prod = String(o.product ?? 'STARS').toUpperCase();
  const isStars = prod === 'STARS';
  const isPremium = prod === 'PREMIUM';
  const starsCount = Number(o.starsQty ?? o.qty ?? 0);
  const monthsCount = Number(o.premiumMonths ?? o.qty ?? 3);

  const badgeKind: 'stars' | 'premium' | 'gram' = isStars
    ? 'stars'
    : isPremium
      ? 'premium'
      : 'gram';
  const badgeIcon = isStars ? 'star' : isPremium ? 'premium' : 'gram';
  const serviceTitle = isStars
    ? `Telegram Stars (${num(starsCount)} шт.)`
    : isPremium
      ? `Telegram Premium (${monthsCount} мес.)`
      : 'Пополнение баланса';

  const totalKopecks =
    Number(o.amountKopecks ?? o.expectedAmountKopecks ?? 0) +
    Number(o.balanceAppliedKopecks ?? 0);
  const st = mapOrderStatusRu(o.status);

  return {
    id,
    shortCode,
    userDisplay,
    userSubId,
    badgeKind,
    badgeIcon,
    serviceTitle,
    totalKopecks,
    statusLabel: st.label,
    statusClass: st.cls,
    relativeTime: formatRelativeTime(o.createdAt),
  };
  // END_BLOCK_NORMALIZE_DASHBOARD_ORDER
}

const CATEGORY_COLORS: Record<string, string> = {
  stars: '#8294ff',
  premium: '#6ec1e4',
  deposit: '#68cba0',
  cases: '#a892ee',
  crash: '#eab875',
};

interface CategoryBreakdownApiResponse {
  from: string | null;
  to: string | null;
  categories: CategoryBreakdownRow[];
  totalOrdersCount: number;
  totalRevenueKopecks: number;
}

type CategoryDatePreset = 'all' | 'today' | '7d' | '30d' | 'custom';

function utcDateOffset(daysAgo: number): string {
  // START_BLOCK_DASHBOARD_UTC_DATE_OFFSET
  return new Date(Date.now() - daysAgo * 86_400_000).toISOString().slice(0, 10);
  // END_BLOCK_DASHBOARD_UTC_DATE_OFFSET
}

function CategoryDonutCard({
  categoryBreakdown,
  money,
  onMore,
}: {
  categoryBreakdown?: CategoryBreakdownRow[];
  money: (kopecks: number) => string;
  onMore: () => void;
}) {
  // START_BLOCK_DASHBOARD_CATEGORY_DONUT
  const [preset, setPreset] = useState<CategoryDatePreset>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const applyPreset = (next: CategoryDatePreset) => {
    setPreset(next);
    if (next === 'all') {
      setFromDate('');
      setToDate('');
    } else if (next === 'today') {
      const today = utcDateOffset(0);
      setFromDate(today);
      setToDate(today);
    } else if (next === '7d') {
      setFromDate(utcDateOffset(6));
      setToDate(utcDateOffset(0));
    } else if (next === '30d') {
      setFromDate(utcDateOffset(29));
      setToDate(utcDateOffset(0));
    }
  };

  const hasDateFilter = Boolean(fromDate || toDate);
  const { data: filteredData, isFetching: filterFetching } = useQuery({
    queryKey: ['dashboard-category-breakdown', fromDate, toDate],
    queryFn: () => {
      const params = new URLSearchParams();
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);
      return api.get<CategoryBreakdownApiResponse>(
        `/admin/stats/categories?${params.toString()}`,
      );
    },
    enabled: hasDateFilter,
  });

  const defaultRows: CategoryBreakdownRow[] = [
    { key: 'stars', label: 'Telegram Stars', ordersCount: 0, revenueKopecks: 0 },
    { key: 'premium', label: 'Telegram Premium', ordersCount: 0, revenueKopecks: 0 },
    { key: 'deposit', label: 'Пополнение баланса', ordersCount: 0, revenueKopecks: 0 },
    { key: 'cases', label: 'Кейсы', ordersCount: 0, revenueKopecks: 0 },
    { key: 'crash', label: 'Crash и Игры', ordersCount: 0, revenueKopecks: 0 },
  ];

  const activeSource = hasDateFilter ? filteredData?.categories : categoryBreakdown;
  const rows =
    Array.isArray(activeSource) && activeSource.length > 0 ? activeSource : defaultRows;

  const segments = rows.map((r) => ({
    key: r.key,
    label: r.label,
    color: CATEGORY_COLORS[r.key] ?? '#8294ff',
    count: Math.max(0, Number(r.ordersCount ?? 0)),
    revenueKopecks: Math.max(0, Number(r.revenueKopecks ?? 0)),
  }));

  const totalCount = segments.reduce((sum, s) => sum + s.count, 0);
  const totalRevenueKopecks = segments.reduce((sum, s) => sum + s.revenueKopecks, 0);
  const size = 224;
  const center = size / 2;
  const radius = 84;
  const strokeWidth = 28;
  const circumference = 2 * Math.PI * radius;
  let cumulative = 0;

  return (
    <div className="card donut-card">
      <div>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Заказы по категориям</div>
          <button type="button" className="btn ghost sm" onClick={onMore}>
            Подробнее
          </button>
        </div>

        <div className="cat-filter-bar">
          <div className="cat-preset-pills">
            {(
              [
                ['today', 'Сегодня'],
                ['7d', '7 дн'],
                ['30d', '30 дн'],
                ['all', 'Всё время'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`cat-preset-btn ${preset === key ? 'active' : ''}`}
                onClick={() => applyPreset(key)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="cat-date-inputs">
            <AdminIcon name="calendar" size={13} style={{ color: 'var(--ink-faint)', flexShrink: 0 }} />
            <input
              type="date"
              aria-label="Дата с"
              value={fromDate}
              onChange={(e) => {
                setPreset('custom');
                setFromDate(e.target.value);
              }}
            />
            <span className="muted" style={{ fontSize: 12 }}>
              —
            </span>
            <input
              type="date"
              aria-label="Дата по"
              value={toDate}
              onChange={(e) => {
                setPreset('custom');
                setToDate(e.target.value);
              }}
            />
          </div>
        </div>
      </div>

      <div className="donut-wrap" style={{ opacity: filterFetching ? 0.65 : 1 }}>
        <div className="donut-ring-box">
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${size} ${size}`}
            style={{ display: 'block' }}
          >
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="#273349"
              strokeWidth={strokeWidth}
            />
            {totalCount > 0 &&
              segments.map((seg) => {
                if (seg.count <= 0) return null;
                const frac = seg.count / totalCount;
                const dash = frac * circumference;
                const offset = -cumulative * circumference;
                cumulative += frac;
                return (
                  <circle
                    key={seg.key}
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={`${dash.toFixed(2)} ${circumference.toFixed(2)}`}
                    strokeDashoffset={offset.toFixed(2)}
                    transform={`rotate(-90 ${center} ${center})`}
                  >
                    <title>{`${seg.label}: ${seg.count} (${(frac * 100).toFixed(1)}%)`}</title>
                  </circle>
                );
              })}
          </svg>
          <div className="donut-center-label">
            <div className="donut-center-val">{num(totalCount)}</div>
            <div className="donut-center-sub">
              {totalCount > 0 ? money(totalRevenueKopecks) : 'нет операций'}
            </div>
          </div>
        </div>

        <div className="donut-legend">
          {segments.map((seg) => {
            const pct = totalCount > 0 ? ((seg.count / totalCount) * 100).toFixed(1) : '0.0';
            return (
              <div className="donut-legend-row" key={seg.key}>
                <span className="donut-legend-left">
                  <span className="donut-dot" style={{ background: seg.color }} />
                  <span>{seg.label}</span>
                </span>
                <span className="donut-pct">
                  {num(seg.count)} <span className="muted">({pct}%)</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
  // END_BLOCK_DASHBOARD_CATEGORY_DONUT
}

// START_CONTRACT: Dashboard
//   PURPOSE: Render the operator Dashboard in MarketPlace dark style with 4 icon KPI cards, charts row, recent orders table, and 3-card right rail.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Polls GET /admin/dashboard and GET /admin/orders every 15s
//   LINKS: M-ADMIN
// END_CONTRACT: Dashboard
export function Dashboard() {
  // START_BLOCK_DASHBOARD
  const [days, setDays] = useState<7 | 14 | 30>(7);
  const { money } = useDisplayCurrency();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', days],
    queryFn: () => api.get<DashboardResponse>(`/admin/dashboard?days=${days}`),
    refetchInterval: 15000,
  });

  const { data: recentOrders } = useQuery({
    queryKey: ['dashboard-recent-orders'],
    queryFn: () => api.get<RawDashboardOrderRow[]>('/admin/orders'),
    refetchInterval: 15000,
  });

  if (isLoading) return <div className="muted">Загрузка операционной сводки…</div>;
  if (error || !data) return <div className="error">Не удалось загрузить дашборд</div>;

  const kpiMap = new Map((data.kpis ?? []).map((k) => [k.key, k]));
  const revKpi = kpiMap.get('gross_revenue');
  const ordersKpi = kpiMap.get('delivered_orders');
  const usersKpi = kpiMap.get('new_users');
  const avgCheckKpi = kpiMap.get('avg_check');

  const byStatus = data.byStatus ?? {};
  const totalOrdersCount = Object.values(byStatus).reduce((a, b) => a + Number(b || 0), 0);
  const deliveredOrdersCount = Number(byStatus['DELIVERED'] ?? 0);
  const avgCheckKopecks =
    deliveredOrdersCount > 0
      ? Math.round(Number(data.revenueKopecks ?? 0) / deliveredOrdersCount)
      : 0;

  const cpuPct = Math.min(
    100,
    Math.max(0, Math.round(Number(data.systemMetrics?.cpuLoadPercent ?? 0))),
  );
  const memPct = Math.min(
    100,
    Math.max(0, Math.round(Number(data.systemMetrics?.memoryUsedPercent ?? 0))),
  );

  const rawOrdersArray = Array.isArray(recentOrders) ? recentOrders : [];
  const ordersList = rawOrdersArray.slice(0, 6).map(normalizeDashboardOrder);
  const reconcile = data.reconcile;
  const sessions = data.sessions ?? { tbank: 'READY', tbankUrl: '', fragment: 'HEALTHY' };
  const queues = data.queues ?? {
    awaitingOrdersCount: byStatus['AWAITING_PAYMENT'] ?? 0,
    stuckOrdersCount: 0,
    unmatchedPaymentsCount: 0,
    openSupportTicketsCount: 0,
    pendingBanAppealsCount: 0,
  };

  const renderBadgeIcon = (kind: 'stars' | 'premium' | 'gram', size = 17) => (
    <AdminIcon
      name={kind === 'stars' ? 'star' : kind === 'premium' ? 'premium' : 'gram'}
      size={size}
    />
  );

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Дашборд</h1>
          <div className="page-subtitle">Общая статистика и аналитика платформы</div>
        </div>
      </div>

      <div className="dashboard-shell">
        {/* ── Left Main Column ── */}
        <div className="dashboard-main">
          {/* 4 Icon KPI Cards */}
          <div className="mp-kpi-grid">
            <div className="mp-kpi-card">
              <div className="mp-kpi-icon purple">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
              </div>
              <div className="mp-kpi-body">
                <div className="mp-kpi-label">Всего заказов</div>
                <div className="mp-kpi-val-row">
                  <span className="mp-kpi-val">{num(totalOrdersCount)}</span>
                  <DeltaPill delta={ordersKpi?.deltaPercent ?? 0} />
                </div>
                <div className="mp-kpi-foot">
                  Выполнено: {num(deliveredOrdersCount)} · за {days} дн: {num(ordersKpi?.value ?? 0)}
                </div>
              </div>
            </div>

            <div className="mp-kpi-card">
              <div className="mp-kpi-icon green">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <path d="M16 12h.01" />
                </svg>
              </div>
              <div className="mp-kpi-body">
                <div className="mp-kpi-label">Общий оборот</div>
                <div className="mp-kpi-val-row">
                  <span className="mp-kpi-val">{money(Number(data.revenueKopecks ?? 0))}</span>
                  <DeltaPill delta={revKpi?.deltaPercent ?? 0} />
                </div>
                <div className="mp-kpi-foot">
                  За {days} дн: {money(Math.round(Number(revKpi?.value ?? 0) * 100))}
                </div>
              </div>
            </div>

            <div className="mp-kpi-card">
              <div className="mp-kpi-icon indigo">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="mp-kpi-body">
                <div className="mp-kpi-label">Пользователи</div>
                <div className="mp-kpi-val-row">
                  <span className="mp-kpi-val">{num(Number(data.users ?? 0))}</span>
                  <DeltaPill delta={usersKpi?.deltaPercent ?? 0} />
                </div>
                <div className="mp-kpi-foot">
                  Новых за {days} дн: +{num(Number(usersKpi?.value ?? 0))}
                </div>
              </div>
            </div>

            <div className="mp-kpi-card">
              <div className="mp-kpi-icon amber">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
              </div>
              <div className="mp-kpi-body">
                <div className="mp-kpi-label">Средний чек</div>
                <div className="mp-kpi-val-row">
                  <span className="mp-kpi-val">{money(avgCheckKopecks)}</span>
                  <DeltaPill delta={avgCheckKpi?.deltaPercent ?? 0} />
                </div>
                <div className="mp-kpi-foot">
                  По выполненным заказам ({num(deliveredOrdersCount)})
                </div>
              </div>
            </div>
          </div>

          {/* Charts Row: Area/Line Chart + Category Donut Chart */}
          <div className="mp-charts-row">
            <DailyStatsChart
              series={Array.isArray(data.dailySeries) ? data.dailySeries : []}
              days={days}
              onChangeDays={(d) => setDays(d)}
            />
            <CategoryDonutCard
              categoryBreakdown={data.categoryBreakdown}
              money={money}
              onMore={() => navigate('/orders')}
            />
          </div>

          {/* Bottom Recent Orders Table Card */}
          <div className="card">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 15.5 }}>Последние заказы</div>
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => navigate('/orders')}
              >
                Все заказы
              </button>
            </div>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>ID заказа</th>
                    <th>Пользователь</th>
                    <th>Услуга</th>
                    <th>Сумма</th>
                    <th>Статус</th>
                    <th>Время</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {ordersList.map((o) => (
                    <tr key={o.id}>
                      <td className="mono" style={{ fontWeight: 600 }}>
                        #{o.shortCode}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{o.userDisplay}</div>
                        <div className="muted" style={{ fontSize: 11.5 }}>
                          ID: {o.userSubId}
                        </div>
                      </td>
                      <td>
                        <div className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
                          <span className={`product-icon-badge ${o.badgeKind}`}>
                            {renderBadgeIcon(o.badgeKind, 18)}
                          </span>
                          <span>{o.serviceTitle}</span>
                        </div>
                      </td>
                      <td style={{ fontWeight: 600 }}>{money(o.totalKopecks)}</td>
                      <td>
                        <span className={`chip ${o.statusClass}`}>{o.statusLabel}</span>
                      </td>
                      <td className="muted" style={{ fontSize: 12.5 }}>
                        {o.relativeTime}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="icon-btn sm"
                          aria-label={`Открыть заказ ${o.shortCode}`}
                          onClick={() => navigate(`/orders?q=${encodeURIComponent(o.shortCode)}`)}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="5" r="1.8" />
                            <circle cx="12" cy="12" r="1.8" />
                            <circle cx="12" cy="19" r="1.8" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {ordersList.length === 0 && (
                    <tr>
                      <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 26 }}>
                        Заказов пока нет — новые заказы появятся здесь автоматически
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Right Rail Column ── */}
        <aside className="dashboard-rail">
          {/* Quick Actions Card */}
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Быстрые действия</div>
            <div className="quick-actions-list">
              <button
                type="button"
                className="quick-action-btn"
                onClick={() => navigate('/balance-adjust')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="16" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
                <span>Создать заказ / Выдача</span>
              </button>

              <button
                type="button"
                className="quick-action-btn"
                onClick={() => navigate('/games')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="16" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
                <span>Добавить услугу / Кейс</span>
              </button>

              <button
                type="button"
                className="quick-action-btn"
                onClick={() => navigate('/auth-sessions')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
                <span>Добавить оператора</span>
              </button>

              <button
                type="button"
                className="quick-action-btn"
                onClick={() => navigate('/broadcasts')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                <span>Рассылка пользователям</span>
              </button>
            </div>
          </div>

          {/* Compact Recent Orders Feed Card */}
          <div className="card">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Последние заказы</div>
              <button
                type="button"
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#a78bfa',
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/orders')}
              >
                Все заказы
              </button>
            </div>

            <div className="feed-list">
              {ordersList.slice(0, 5).map((o) => (
                <div className="feed-item" key={o.id}>
                  <div className="feed-item-left">
                    <span className={`product-icon-badge ${o.badgeKind}`}>
                      {renderBadgeIcon(o.badgeKind, 17)}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        #{o.shortCode}
                      </div>
                      <div
                        className="muted"
                        style={{
                          fontSize: 11.5,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {o.serviceTitle}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>
                      {money(o.totalKopecks)}
                    </div>
                    <div style={{ marginTop: 3 }}>
                      <span className={`chip ${o.statusClass}`} style={{ padding: '2px 7px', fontSize: 10.5 }}>
                        {o.statusLabel}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {ordersList.length === 0 && (
                <div className="muted" style={{ fontSize: 12.5, textAlign: 'center', padding: 12 }}>
                  Активных заказов нет
                </div>
              )}
            </div>
          </div>

          {/* System Status Card */}
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Статус системы</div>
            <div className="sys-status-list">
              <div className="sys-status-row">
                <span className="sys-status-label">API сервисов</span>
                <span className="sys-status-val">Работает</span>
              </div>
              <div className="sys-status-row">
                <span className="sys-status-label">Платежная система</span>
                <span
                  className={`sys-status-val ${
                    sessions.tbank === 'READY' ||
                    sessions.tbank === 'ACTIVE' ||
                    sessions.tbank === 'HEALTHY'
                      ? ''
                      : 'warn'
                  }`}
                >
                  {sessions.tbank === 'READY' ||
                  sessions.tbank === 'ACTIVE' ||
                  sessions.tbank === 'HEALTHY'
                    ? 'Работает'
                    : sessions.tbank || 'Ожидание'}
                </span>
              </div>
              <div className="sys-status-row">
                <span className="sys-status-label">Очередь заказов</span>
                <span className={`sys-status-val ${queues.stuckOrdersCount > 0 ? 'warn' : ''}`}>
                  {queues.stuckOrdersCount > 0
                    ? `В очереди (${queues.stuckOrdersCount})`
                    : 'Работает'}
                </span>
              </div>
              <div className="sys-status-row">
                <span className="sys-status-label">База данных</span>
                <span className={`sys-status-val ${reconcile?.healthy === false ? 'warn' : ''}`}>
                  {reconcile?.healthy === false ? 'Требует сверки' : 'Работает'}
                </span>
              </div>

              <div className="sys-bar-wrap">
                <div className="sys-bar-head">
                  <span>Нагрузка CPU сервера</span>
                  <span style={{ color: 'var(--ink)' }}>{cpuPct}%</span>
                </div>
                <div className="sys-bar-track">
                  <div className="sys-bar-fill" style={{ width: `${cpuPct}%` }} />
                </div>
              </div>

              <div className="sys-bar-wrap">
                <div className="sys-bar-head">
                  <span>Использование памяти ОС</span>
                  <span style={{ color: 'var(--ink)' }}>{memPct}%</span>
                </div>
                <div className="sys-bar-track">
                  <div className="sys-bar-fill" style={{ width: `${memPct}%` }} />
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
  // END_BLOCK_DASHBOARD
}
